import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import colors from '../../constants/colors';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    isDigital: false,
    link: '',
    order: 0,
    hasVariants: false
  });
  
  const [variants, setVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validated, setValidated] = useState(false);
  
  useEffect(() => {
    // Check if user is admin
    if (!user || !user.isAdmin) {
      navigate('/');
      return;
    }
    
    // If in edit mode, fetch product data
    if (isEditMode) {
      const fetchProduct = async () => {
        try {
          setLoading(true);
          const response = await axiosInstance.get(`/product/${id}`);
          const productData = response;
          
          setFormData({
            name: productData.name || '',
            description: productData.description || '',
            price: productData.price || '',
            stock: productData.stock || '',
            isDigital: productData.isDigital || false,
            link: productData.link || '',
            order: productData.order || 0,
            hasVariants: productData.hasVariants || false
          });
          
          if (productData.variants) {
            setVariants(productData.variants);
          }
          
          if (productData.images && productData.images.length > 0) {
            setExistingImages(productData.images);
          }
          
        } catch (err) {
          console.error('Error fetching product:', err);
          setError('Erro ao carregar dados do produto.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchProduct();
    }
  }, [id, isEditMode, user, navigate]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleVariantChange = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index] = {
      ...newVariants[index],
      [field]: value
    };
    setVariants(newVariants);
  };
  
  const addVariant = () => {
    setVariants([
      ...variants,
      {
        name: '',
        price: '',
        stock: '',
        sku: ''
      }
    ]);
  };
  
  const removeVariant = (index) => {
    const newVariants = variants.filter((_, i) => i !== index);
    setVariants(newVariants);
  };
  
  const handleImagesChange = (e) => {
    const fileList = Array.from(e.target.files);
    setImages(fileList);
  };
  
  const handleRemoveExistingImage = (imageId) => {
    setExistingImages(prevImages => prevImages.filter(img => img.id !== imageId));
    setImagesToRemove(prev => [...prev, imageId]);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('stock', formData.stock);
      formDataToSend.append('isDigital', formData.isDigital);
      formDataToSend.append('link', formData.link);
      formDataToSend.append('order', formData.order);
      formDataToSend.append('hasVariants', formData.hasVariants);
      
      if (formData.hasVariants) {
        formDataToSend.append('variants', JSON.stringify(variants));
      }
      
      images.forEach(image => {
        formDataToSend.append('images', image);
      });
      
      if (imagesToRemove.length > 0) {
        formDataToSend.append('imagesToRemove', JSON.stringify(imagesToRemove));
      }
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      if (isEditMode) {
        await axiosInstance.put(`/product/${id}`, formDataToSend, config);
      } else {
        await axiosInstance.post('/product', formDataToSend, config);
      }
      
      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Erro ao salvar produto. Por favor tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && isEditMode) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" style={{ color: colors.secondary }}
        />
      </Container>
    );
  }
  
  return (
    <Container className="py-5">
      <h1 className="mb-4">{isEditMode ? 'Editar Produto' : 'Novo Produto'}</h1>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insira o nome do produto.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insira a descrição do produto.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox" 
                    label="Produto tem variantes" 
                    name="hasVariants" 
                    checked={formData.hasVariants} 
                    onChange={handleChange} 
                  />
                </Form.Group>

                {!formData.hasVariants ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Preço</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira o preço do produto.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Stock</Form.Label>
                      <Form.Control
                        type="number"
                        name="stock"
                        value={formData.stock}
                        onChange={handleChange}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira o stock do produto.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </>
                ) : (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5>Variantes</h5>
                      <Button variant="outline-primary" onClick={addVariant}>
                        Adicionar Variante
                      </Button>
                    </div>
                    
                    {variants.map((variant, index) => (
                      <Card key={index} className="mb-3">
                        <Card.Body>
                          <div className="d-flex justify-content-between mb-3">
                            <h6>Variante {index + 1}</h6>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => removeVariant(index)}
                            >
                              Remover
                            </Button>
                          </div>
                          
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Nome</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={variant.name}
                                  onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>SKU</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={variant.sku}
                                  onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Preço</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  value={variant.price}
                                  onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Stock</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={variant.stock}
                                  onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                                  required
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox" 
                    label="Produto Digital" 
                    name="isDigital" 
                    checked={formData.isDigital} 
                    onChange={handleChange} 
                  />
                </Form.Group>
                
                {formData.isDigital && (
                  <Form.Group className="mb-3">
                    <Form.Label>Link de Download</Form.Label>
                    <Form.Control
                      type="text"
                      name="link"
                      value={formData.link}
                      onChange={handleChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Por favor insira o link de download.
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label>Ordem</Form.Label>
                  <Form.Control
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insira a ordem.
                  </Form.Control.Feedback>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4}>
            <Card className="mb-4">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Imagens</Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImagesChange}
                  />
                </Form.Group>
                
                {existingImages.length > 0 && (
                  <div className="mb-3">
                    <h6>Imagens Existentes</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {existingImages.map(image => (
                        <Col key={image.id} xs={4} md={3} className="mb-3">
                          <Card className="position-relative">
                            <Card.Img 
                              variant="top" 
                              src={image.src} 
                              alt="Imagem do produto"
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 m-1"
                              onClick={() => handleRemoveExistingImage(image.id)}
                            >
                              ×
                            </Button>
                          </Card>
                        </Col>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/admin-dashboard')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default ProductForm;
